import { MediaStreamConnector, ConnectParams, MediaStreamConnection } from './MediaStreamConnector';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { isReconnectable } from './ReconnectableConnector';

export class TransformingConnector<T extends Transformer> implements MediaStreamConnector {
    protected readonly transformingReceivers = new Map<RTCRtpReceiver, T | undefined>();

    constructor(
        private readonly underlying: MediaStreamConnector,
        private readonly createTransformer: (receiver: RTCRtpReceiver) => T | undefined
    ) { }

    async connect(params: ConnectParams): Promise<MediaStreamConnection> {
        return await this.underlying.connect(params);
    }

    async disconnect(): Promise<void> {
        await this.underlying.disconnect();
    }

    private listenerManager = createListenerManager<(connection: MediaStreamConnection) => void>(listeners => {
        const underlying = this.underlying;
        if (isReconnectable(underlying)) {
            const listener = (connection: MediaStreamConnection) => {
                connection.stream.getTracks().forEach(track => {
                    const receiver = connection.lookupReceiver(track);
                    if (receiver) {
                        this.setupReceiverTransform(receiver);
                    }
                });

                listeners.forEach(cb => cb(connection));
            };
            underlying.addReconnectListener(listener);
            return () => underlying.removeReconnectListener(listener);
        } else {
            return () => 0;
        }
    });

    addReconnectListener(listener: (connection: MediaStreamConnection) => void): void {
        this.listenerManager.addListener(listener);
    }

    removeReconnectListener(listener: (connection: MediaStreamConnection) => void): void {
        this.listenerManager.removeListener(listener);
    }

    private setupReceiverTransform(receiver: RTCRtpReceiver) {
        if (!receiver.createEncodedStreams) {
            return;
        }

        if (this.transformingReceivers.has(receiver)) {
            return;
        }
        const transformer = this.createTransformer(receiver);
        this.transformingReceivers.set(receiver, transformer);

        const receiverStreams = receiver.createEncodedStreams();
        const readableStream = receiverStreams.readable;
        const writableStream = receiverStreams.writable;

        const transformStream = new TransformStream(transformer);
        readableStream
            .pipeThrough(transformStream)
            .pipeTo(writableStream);
    }
}
